import { useState, useEffect } from 'react'
import { Command } from 'cmdk'
import { useNavigate } from 'react-router-dom'
import { 
  Search, LayoutDashboard, CheckSquare, Calendar as CalendarIcon, 
  Activity, Wallet, Dumbbell, Flame, Moon, Sun, Settings, Clock
} from 'lucide-react'
import { useIdioma } from '../context/IdiomaContext'

export default function CommandPalette() {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const { t } = useIdioma()

  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((open) => !open)
      }
    }
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [])

  const runCommand = (command) => {
    setOpen(false)
    command()
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] sm:pt-[20vh] px-4">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm dark:bg-gray-900/60 transition-opacity"
        onClick={() => setOpen(false)}
      />

      <Command 
        className="relative z-[101] w-full max-w-2xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden"
        loop
      >
        <div className="flex items-center px-4 py-3 border-b border-gray-100 dark:border-gray-800">
          <Search className="w-5 h-5 text-gray-400 mr-3" />
          <Command.Input 
            autoFocus
            placeholder={t('search_commands') || "Buscar módulos o comandos..."}
            className="flex-1 bg-transparent text-gray-900 dark:text-white outline-none placeholder:text-gray-400 text-lg" 
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
            ESC
          </kbd>
        </div>

        <Command.List className="max-h-[300px] overflow-y-auto p-2 scrollbar-hide">
          <Command.Empty className="py-6 text-center text-sm text-gray-500">
            No se encontraron resultados.
          </Command.Empty>

          <Command.Group heading="Navegación" className="px-2 text-xs font-medium text-gray-500 py-2">
            <Command.Item 
              onSelect={() => runCommand(() => navigate('/'))}
              className="flex items-center px-3 py-2 mt-1 rounded-xl text-sm text-gray-700 dark:text-gray-200 cursor-pointer aria-selected:bg-indigo-50 dark:aria-selected:bg-indigo-500/10 aria-selected:text-indigo-600 dark:aria-selected:text-indigo-400"
            >
              <LayoutDashboard className="w-4 h-4 mr-3" />
              Dashboard
            </Command.Item>
            <Command.Item 
              onSelect={() => runCommand(() => navigate('/tareas'))}
              className="flex items-center px-3 py-2 mt-1 rounded-xl text-sm text-gray-700 dark:text-gray-200 cursor-pointer aria-selected:bg-indigo-50 dark:aria-selected:bg-indigo-500/10 aria-selected:text-indigo-600 dark:aria-selected:text-indigo-400"
            >
              <CheckSquare className="w-4 h-4 mr-3" />
              Tareas
            </Command.Item>
            <Command.Item 
              onSelect={() => runCommand(() => navigate('/calendario'))}
              className="flex items-center px-3 py-2 mt-1 rounded-xl text-sm text-gray-700 dark:text-gray-200 cursor-pointer aria-selected:bg-indigo-50 dark:aria-selected:bg-indigo-500/10 aria-selected:text-indigo-600 dark:aria-selected:text-indigo-400"
            >
              <CalendarIcon className="w-4 h-4 mr-3" />
              Calendario
            </Command.Item>
            <Command.Item
              onSelect={() => runCommand(() => navigate('/planificador'))}
              className="flex items-center px-3 py-2 mt-1 rounded-xl text-sm text-gray-700 dark:text-gray-200 cursor-pointer aria-selected:bg-indigo-50 dark:aria-selected:bg-indigo-500/10 aria-selected:text-indigo-600 dark:aria-selected:text-indigo-400"
            >
              <Clock className="w-4 h-4 mr-3" />
              Planificador
            </Command.Item>
            <Command.Item 
              onSelect={() => runCommand(() => navigate('/habitos'))}
              className="flex items-center px-3 py-2 mt-1 rounded-xl text-sm text-gray-700 dark:text-gray-200 cursor-pointer aria-selected:bg-indigo-50 dark:aria-selected:bg-indigo-500/10 aria-selected:text-indigo-600 dark:aria-selected:text-indigo-400"
            >
              <Activity className="w-4 h-4 mr-3" />
              Hábitos
            </Command.Item>
            <Command.Item 
              onSelect={() => runCommand(() => navigate('/finanzas'))}
              className="flex items-center px-3 py-2 mt-1 rounded-xl text-sm text-gray-700 dark:text-gray-200 cursor-pointer aria-selected:bg-indigo-50 dark:aria-selected:bg-indigo-500/10 aria-selected:text-indigo-600 dark:aria-selected:text-indigo-400"
            >
              <Wallet className="w-4 h-4 mr-3" />
              Finanzas
            </Command.Item>
            <Command.Item 
              onSelect={() => runCommand(() => navigate('/gimnasio'))}
              className="flex items-center px-3 py-2 mt-1 rounded-xl text-sm text-gray-700 dark:text-gray-200 cursor-pointer aria-selected:bg-indigo-50 dark:aria-selected:bg-indigo-500/10 aria-selected:text-indigo-600 dark:aria-selected:text-indigo-400"
            >
              <Dumbbell className="w-4 h-4 mr-3" />
              Gimnasio
            </Command.Item>
            <Command.Item 
              onSelect={() => runCommand(() => navigate('/calorias'))}
              className="flex items-center px-3 py-2 mt-1 rounded-xl text-sm text-gray-700 dark:text-gray-200 cursor-pointer aria-selected:bg-indigo-50 dark:aria-selected:bg-indigo-500/10 aria-selected:text-indigo-600 dark:aria-selected:text-indigo-400"
            >
              <Flame className="w-4 h-4 mr-3" />
              Calorías
            </Command.Item>
          </Command.Group>

          <Command.Group heading="Acciones" className="px-2 text-xs font-medium text-gray-500 py-2 border-t border-gray-100 dark:border-gray-800">
            <Command.Item 
              onSelect={() => runCommand(() => document.documentElement.classList.toggle('dark'))}
              className="flex items-center px-3 py-2 mt-1 rounded-xl text-sm text-gray-700 dark:text-gray-200 cursor-pointer aria-selected:bg-indigo-50 dark:aria-selected:bg-indigo-500/10 aria-selected:text-indigo-600 dark:aria-selected:text-indigo-400"
            >
              <Moon className="w-4 h-4 mr-3 hidden dark:block" />
              <Sun className="w-4 h-4 mr-3 block dark:hidden" />
              Cambiar Tema (Claro/Oscuro)
            </Command.Item>
            <Command.Item 
              onSelect={() => runCommand(() => navigate('/logros'))}
              className="flex items-center px-3 py-2 mt-1 rounded-xl text-sm text-gray-700 dark:text-gray-200 cursor-pointer aria-selected:bg-indigo-50 dark:aria-selected:bg-indigo-500/10 aria-selected:text-indigo-600 dark:aria-selected:text-indigo-400"
            >
              <Settings className="w-4 h-4 mr-3" />
              Configuración y Logros
            </Command.Item>
          </Command.Group>
        </Command.List>
      </Command>
    </div>
  )
}
